import { fmtNum } from "@/shared/lib/format";
import type { PlatformProfile } from "../mockData";

// ─── Shared insight ───────────────────────────────────────────────────────────

type InsVariant = "ig" | "ib" | "ia";
const INS_COLOR: Record<InsVariant, string> = { ig: "#639922", ib: "#378ADD", ia: "#BA7517" };

function InfoSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
  );
}
function SparklesSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
    </svg>
  );
}
function WarnSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}

function Ins({ variant, text }: { variant: InsVariant; text: string }) {
  const Icon = variant === "ig" ? SparklesSvg : variant === "ib" ? InfoSvg : WarnSvg;
  return (
    <div className="flex items-start gap-[5px] px-[10px] py-[7px] rounded-md mt-[10px] text-[11px]"
      style={{ background: "rgba(28,28,28,0.04)", color: "rgba(28,28,28,0.55)" }}>
      <span style={{ color: INS_COLOR[variant], flexShrink: 0, marginTop: 1 }}>
        <Icon size={12} />
      </span>
      <span className="leading-snug">{text}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-[10px] font-display font-bold uppercase tracking-wider text-ink-40 mb-2">{children}</div>;
}

// ─── Sparkline ────────────────────────────────────────────────────────────────

function Sparkline({ data }: { data: number[] }) {
  const max = Math.max(...data);
  return (
    <div>
      <div className="flex items-end gap-[3px] h-[40px]">
        {data.map((v, i) => {
          const h = Math.max(4, Math.round((v / max) * 40));
          const isLast = i === data.length - 1;
          return (
            <div
              key={i}
              className="flex-1 cursor-pointer transition-colors"
              style={{
                height: h,
                background: isLast ? "#639922" : "#C0DD97",
                borderRadius: "2px 2px 0 0",
              }}
              title={`${fmtNum(v)} просмотров`}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = "#639922"; }}
              onMouseLeave={(e) => {
                if (!isLast) (e.currentTarget as HTMLDivElement).style.background = "#C0DD97";
              }}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-[10px] text-ink-40 mt-1">
        <span>раньше</span><span>новее</span>
      </div>
    </div>
  );
}

// ─── Format reach table ───────────────────────────────────────────────────────

function FormatReachTable({ rows, platform }: { rows: { format: string; range: string }[]; platform: string }) {
  const isTg = platform === "tg";
  return (
    <div>
      <SectionLabel>Охват по форматам</SectionLabel>
      <div className="divide-y" style={{ borderColor: "rgba(28,28,28,0.08)" }}>
        {rows.map((r) => (
          <div key={r.format} className="flex items-center justify-between py-2">
            <span className="text-[12px] text-ink-40">{r.format}</span>
            <span className="text-[12px] font-[500] font-mono">{r.range}</span>
          </div>
        ))}
      </div>
      <Ins variant="ib" text={isTg
        ? "Более длинные размещения дают больший охват за счёт повторных просмотров ленты."
        : "Клипы дают максимальный охват за счёт рекомендательного алгоритма VK."} />
    </div>
  );
}

// ─── Ad load ─────────────────────────────────────────────────────────────────

function AdLoad({ adsPerMonth }: { adsPerMonth: number }) {
  const isHigh = adsPerMonth >= 8;
  const isMed = adsPerMonth >= 4;
  return (
    <div>
      <SectionLabel>Рекламная нагрузка</SectionLabel>
      <div className="grid grid-cols-3 gap-2">
        {[
          { v: adsPerMonth, label: "интеграций/мес" },
          { v: Math.round(adsPerMonth * 0.6), label: "посев" },
          { v: Math.round(adsPerMonth * 0.4), label: "натив" },
        ].map(({ v, label }) => (
          <div key={label} className="rounded-lg p-3 text-center" style={{ background: "rgba(28,28,28,0.04)" }}>
            <div className="font-display font-[500] text-[18px] leading-tight">{v}</div>
            <div className="text-[10px] text-ink-40 mt-0.5">{label}</div>
          </div>
        ))}
      </div>
      <Ins variant={isHigh ? "ia" : "ig"} text={
        isHigh
          ? `Высокая нагрузка (${adsPerMonth}/мес) — аудитория может быть выгоревшей.`
          : isMed
          ? `Средняя нагрузка (${adsPerMonth}/мес) — рекомендуем проверить реакции на рекламные посты.`
          : `Умеренная нагрузка (${adsPerMonth}/мес) — аудитория восприимчива к рекламе.`
      } />
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export function ReachTab({ data }: { data: PlatformProfile }) {
  return (
    <div className="grid gap-6">
      <div>
        <SectionLabel>Динамика охвата · последние 12 постов</SectionLabel>
        <Sparkline data={data.sparkline} />
      </div>
      <FormatReachTable rows={data.formatReach} platform={data.key} />
      <AdLoad adsPerMonth={data.adsPerMonth} />
    </div>
  );
}
